import React, { forwardRef, useRef } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import ja from 'date-fns/locale/ja';
import IconButton from '../../atoms/IconButton';
import Button from '../../atoms/Button';
import { dateToFormatString } from '../../../Common';
import 'moment/locale/ja';
import Label from '../../atoms/Label';
import holidayJp from '@holiday-jp/holiday_jp';

interface VisitDatePickerProps {
  className?: string;
  value?: Date | undefined;
  disabled?: boolean | undefined;
  isClearable?: boolean | undefined;
  placeholder?: string;
  inquiryDate: Date;
  updateDate(date: Date): void;
}

const VisitDatePicker: React.FC<VisitDatePickerProps> = ({ className, value, disabled, isClearable, inquiryDate, updateDate }) => {
  const datepickerRef = useRef<React.ElementRef<typeof DatePicker>>(null);
  const DatepickerInput = forwardRef<HTMLButtonElement>(({ ...props }, ref) => (
    <IconButton className='u-float-right u-mn8' icon='today' customRef={ref} {...props} />
  ));
  const TodayButton = ({ ...props }) => (
    <div onClick={(e) => e.stopPropagation()} className='today-button__wrapper'>
      <button
        className='today-button__button'
        disabled={disabled}
        type='button'
        onClick={(e) => {
          e.stopPropagation();
          updateDate(inquiryDate);
          datepickerRef.current?.setOpen(false);
        }}
        data-testid='visit-date-picker--today-button'
        {...props}>
        今日
      </button>
    </div>
  );

  const leftButtonClick = () => {
    let date;
    if (value) {
      date = new Date(value);
      date.setDate(date.getDate() - 1);
      updateDate(date);
    }
  };

  const rightButtonClick = () => {
    let date;
    if (value) {
      date = new Date(value);
      date.setDate(date.getDate() + 1);
      updateDate(date);
    }
  };

  registerLocale('ja', ja);

  return (
    <div className='u-mb16 u-flex u-items-center'>
      <Button
        type='secondary'
        iconClassName='visit-button-icon'
        leftIcon='keyboard_arrow_left'
        className='u-w4s'
        onClick={leftButtonClick}
        data-testid='visit-date-picker--left-button'
      />
      <Label
        className='u-text-l u-mh16'
        label={dateToFormatString(value, 'yyyy年MM月dd日(E)')}
        data-testid='visit-date-picker--date-label'
      />
      <Button
        type='secondary'
        iconClassName='visit-button-icon'
        leftIcon='keyboard_arrow_right'
        className='u-w4s'
        onClick={rightButtonClick}
        data-testid='visit-date-picker--right-button'
      />
      <DatePicker
        ref={datepickerRef}
        className={className}
        wrapperClassName='u-w-auto'
        dateFormat={'yyyy/MM/dd'}
        isClearable={isClearable}
        selected={value ? new Date(value) : inquiryDate}
        locale={'ja'}
        customInput={<DatepickerInput />}
        onChange={(dt: Date) => {
          updateDate(dt);
        }}
        todayButton={<TodayButton />}
        clearButtonTitle={'クリア'}
        disabled={disabled}
        calendarClassName='weekend-calendar'
        dayClassName={(date: Date): string | null => {
          return holidayJp.isHoliday(date) ? 'holiday' : null;
        }}
      />
    </div>
  );
};
export default VisitDatePicker;
